import { useState, type CSSProperties, type ReactNode } from 'react';
import { ResourcesBook } from '../components/guild/SpellBook';
import { ReadinessBook } from '../components/guild/ReadinessBook';
import { RESOURCES, type Resource } from '../components/guild/ResourceGrid';
import { RampartsBackdrop } from '../components/guild/RampartsBackdrop';
import { TITLE_FONT, PX_FONT } from '../components/guild/theme';

/* Concepts for the pixel skin's inner panels, each shown on the Resources section. */

const INK = '#1A1014';
// Chunky pixel bevel: ink outline, lit top-left edge, shaded bottom-right edge
const bevel = (light: string, dark: string, ink = INK) =>
  `0 0 0 2px ${ink}, inset 2px 2px 0 ${light}, inset -2px -2px 0 ${dark}`;
// Outline that follows a clip-path: put it on a wrapper as a filter
const inkOutline = (ink = INK) =>
  `drop-shadow(2px 0 0 ${ink}) drop-shadow(-2px 0 0 ${ink}) drop-shadow(0 2px 0 ${ink}) drop-shadow(0 -2px 0 ${ink})`;

const go = (r: Resource) => (r.url ? { href: r.url, target: '_blank', rel: 'noreferrer' } : {});

/* Ribbon title with forked ends, like the kit's section headers */
function Ribbon({ children, fill = '#8E3B3B', shade = '#5E2428' }: { children: ReactNode; fill?: string; shade?: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 18, filter: inkOutline() }}>
      <div style={{
        padding: '8px 44px 10px', fontFamily: PX_FONT, fontSize: 14, letterSpacing: '0.16em', color: '#F6E7C8', textTransform: 'uppercase',
        background: `linear-gradient(0deg, ${shade} 0 4px, ${fill} 4px)`,
        clipPath: 'polygon(0 0, 100% 0, 94% 50%, 100% 100%, 0 100%, 6% 50%)',
        textShadow: `0 2px 0 ${shade}`,
      }}>{children}</div>
    </div>
  );
}

/* 1 · Quest board: parchment notes pinned to a planked board */
function QuestBoard() {
  return (
    <div style={{
      padding: '20px 22px 26px',
      background: 'repeating-linear-gradient(180deg, #6E4628 0 30px, #5A3820 30px 32px), #6E4628',
      boxShadow: bevel('#8F6038', '#3E2614'),
    }}>
      <Ribbon>Quest Board</Ribbon>
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 18 }}>
        {RESOURCES.map((r, i) => (
          <a key={r.key} {...go(r)} className="lab-note" style={{
            position: 'relative', width: 128, padding: '20px 10px 12px', textDecoration: 'none', textAlign: 'center',
            background: 'linear-gradient(180deg, #F0DDB0, #E3C68E)', color: '#3A2416',
            boxShadow: bevel('#FBEFD2', '#C9A46A', '#3A2416'),
            transform: `rotate(${[-2, 1.5, -1, 2, -1.5, 1, -2.5][i % 7]}deg)`, opacity: r.url ? 1 : 0.7,
          }}>
            <span style={{ position: 'absolute', top: 6, left: '50%', width: 8, height: 8, marginLeft: -4, background: '#B23A2E', boxShadow: `0 0 0 2px ${INK}, inset -2px -2px 0 #7A2018` }} />
            <span style={{ display: 'block', color: '#5B3520' }}>{r.icon}</span>
            <span style={{ display: 'block', fontFamily: TITLE_FONT, fontSize: 16, marginTop: 6 }}>{r.name}</span>
            <span style={{ display: 'block', fontFamily: PX_FONT, fontSize: 9, marginTop: 4, color: '#7A5434' }}>{r.url ? r.blurb : 'Link coming'}</span>
          </a>
        ))}
      </div>
    </div>
  );
}

/* 2 · Satchel: ornate inventory slots, name on hover like an item tooltip */
function Satchel() {
  return (
    <div style={{ padding: '20px 22px 26px', background: '#3B2C33', boxShadow: bevel('#5A4450', '#241A20') }}>
      <Ribbon fill="#5A6E8C" shade="#3A4A62">Satchel</Ribbon>
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 14 }}>
        {RESOURCES.map(r => (
          <a key={r.key} {...go(r)} className="lab-slot" data-tip={`${r.name} · ${r.url ? r.blurb : 'link coming'}`} style={{
            position: 'relative', width: 78, height: 78, display: 'grid', placeItems: 'center', textDecoration: 'none',
            background: 'radial-gradient(circle, #2A1E24 0 55%, #1E151A 100%)', color: '#F1DDB0',
            boxShadow: `${bevel('#C9A05A', '#7A5A2C')}, inset 0 0 0 4px #3B2C33, inset 0 0 0 6px #1A1014`,
            opacity: r.url ? 1 : 0.55,
          }}>
            {/* Brass corner studs */}
            {[[4, 4], [62, 4], [4, 62], [62, 62]].map(([x, y]) => (
              <span key={`${x}${y}`} style={{ position: 'absolute', left: x, top: y, width: 6, height: 6, background: '#E2B86A', boxShadow: `0 0 0 1px ${INK}` }} />
            ))}
            {r.icon}
          </a>
        ))}
      </div>
      <p style={{ textAlign: 'center', margin: '14px 0 0', fontFamily: PX_FONT, fontSize: 10, color: '#C9B4A0' }}>Hover an item for its name</p>
    </div>
  );
}

/* 3 · Tavern signs: painted boards hanging on chains from an iron rail */
function TavernSigns() {
  return (
    <div style={{ padding: '20px 22px 26px', background: 'rgba(8,20,26,0.55)', boxShadow: bevel('#2E4A52', '#06121A') }}>
      <Ribbon fill="#6E4628" shade="#3E2614">Market Row</Ribbon>
      <div style={{ height: 6, margin: '0 6px', background: '#2C2C34', boxShadow: `0 0 0 2px ${INK}, inset 0 2px 0 #4A4A56` }} />
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 16 }}>
        {RESOURCES.map(r => (
          <a key={r.key} {...go(r)} className="lab-sign" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textDecoration: 'none', transformOrigin: '50% 0', opacity: r.url ? 1 : 0.6 }}>
            <span style={{ display: 'flex', gap: 60 }}>
              {[0, 1].map(k => <span key={k} style={{ width: 2, height: 22, background: 'repeating-linear-gradient(180deg, #8A8A96 0 4px, transparent 4px 6px)' }} />)}
            </span>
            <span style={{
              width: 116, padding: '12px 8px 10px', textAlign: 'center',
              background: 'repeating-linear-gradient(180deg, #8A5A32 0 12px, #7A4E2A 12px 14px)',
              boxShadow: bevel('#A8744A', '#4E301A'), color: '#F3E2BC',
            }}>
              <span style={{ display: 'block', filter: 'drop-shadow(0 2px 0 #3E2614)' }}>{r.icon}</span>
              <span style={{ display: 'block', fontFamily: TITLE_FONT, fontSize: 15, marginTop: 4, textShadow: '0 2px 0 #3E2614' }}>{r.name}</span>
            </span>
          </a>
        ))}
      </div>
    </div>
  );
}

/* 4 · Stained glass: arched panes glowing in moonlight, a stone sill with the name */
const GLASS = ['#2E7C8C', '#3E5FA8', '#2F8F7A', '#7A4FA0', '#A86A2E', '#3E5FA8', '#2E7C8C'];
function StainedGlass() {
  const arch = 'polygon(0 100%, 0 34%, 4% 22%, 12% 11%, 24% 4%, 38% 0, 62% 0, 76% 4%, 88% 11%, 96% 22%, 100% 34%, 100% 100%)';
  return (
    <div style={{ padding: '20px 22px 26px', background: 'rgba(8,20,26,0.55)', boxShadow: bevel('#2E4A52', '#06121A') }}>
      <Ribbon fill="#3E5F7A" shade="#24384A">Chapel Windows</Ribbon>
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 18 }}>
        {RESOURCES.map((r, i) => (
          <a key={r.key} {...go(r)} className="lab-glass" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textDecoration: 'none', opacity: r.url ? 1 : 0.55 }}>
            <span style={{ filter: `${inkOutline('#0E0A10')} drop-shadow(0 0 10px ${GLASS[i]}88)` }}>
              <span style={{
                width: 84, height: 108, display: 'grid', placeItems: 'center', color: '#F4F8FA', clipPath: arch,
                background: `
                  linear-gradient(90deg, transparent 49%, #0E0A10 49% 51%, transparent 51%),
                  linear-gradient(0deg, transparent 32%, #0E0A10 32% 34%, transparent 34% 66%, #0E0A10 66% 68%, transparent 68%),
                  linear-gradient(135deg, ${GLASS[i]} 0 40%, ${GLASS[(i + 2) % GLASS.length]} 40% 70%, ${GLASS[(i + 4) % GLASS.length]} 70%)`,
              }}>
                <span style={{ filter: 'drop-shadow(0 0 2px #0E0A10) drop-shadow(0 0 1px #0E0A10)' }}>{r.icon}</span>
              </span>
            </span>
            <span style={{
              marginTop: 6, width: 96, padding: '4px 0 6px', textAlign: 'center', fontFamily: TITLE_FONT, fontSize: 14, color: '#DCE6E8',
              background: '#3A4A50', boxShadow: bevel('#5E7278', '#1E2A2E', '#0E0A10'),
            }}>{r.name}</span>
          </a>
        ))}
      </div>
    </div>
  );
}

const CONCEPTS: { name: string; note: string; body: ReactNode }[] = [
  { name: 'Quest board', note: 'Parchment notes pinned to a planked board', body: <QuestBoard /> },
  { name: 'Satchel', note: 'Ornate inventory slots; the name shows on hover like an item tooltip', body: <Satchel /> },
  { name: 'Tavern signs', note: 'Painted boards on chains from an iron rail; they sway on hover', body: <TavernSigns /> },
  { name: 'Stained glass', note: 'Arched chapel panes glowing in moonlight, names on a stone sill', body: <StainedGlass /> },
];

const wrap: CSSProperties = { position: 'relative', zIndex: 1, maxWidth: 1180, margin: '0 auto', padding: '32px 24px', display: 'flex', flexDirection: 'column', gap: 30 };

export function PaletteLab() {
  const [replay, setReplay] = useState(0);
  const [closed, setClosed] = useState(false);
  const [which, setWhich] = useState<'resources' | 'readiness'>('readiness');
  return (
    <div style={{ position: 'relative', minHeight: '100vh' }}>
      <RampartsBackdrop />
      <style>{`
        .lab-note { transition: transform 0.15s steps(2); }
        .lab-note:hover { transform: rotate(0deg) translateY(-3px) !important; }
        .lab-slot:hover { box-shadow: 0 0 0 2px #1A1014, inset 2px 2px 0 #F2CF86, inset -2px -2px 0 #9A7438, inset 0 0 0 4px #3B2C33, inset 0 0 0 6px #1A1014 !important; }
        .lab-slot:hover::after {
          content: attr(data-tip); position: absolute; left: 50%; top: calc(100% + 8px); transform: translateX(-50%); white-space: nowrap; z-index: 2;
          padding: 5px 9px; font: 10px "Silkscreen", monospace; color: #F6E7C8; background: #241A20; box-shadow: 0 0 0 2px #1A1014, inset 2px 2px 0 #5A4450;
        }
        @keyframes sign-sway { 0%, 100% { transform: rotate(0deg); } 30% { transform: rotate(2.5deg); } 70% { transform: rotate(-2deg); } }
        .lab-sign:hover { animation: sign-sway 0.9s steps(6) 1; }
        .lab-glass:hover > span:first-child { filter: brightness(1.25) drop-shadow(0 0 14px #9FE3F088) !important; }
      `}</style>
      <main style={wrap}>
        <section>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 14, marginBottom: 10 }}>
            <span style={{ fontFamily: TITLE_FONT, fontSize: 26, color: '#EEF5F7', textShadow: '0 2px 0 #020A10' }}>★ Spellbook</span>
            <span style={{ fontFamily: PX_FONT, fontSize: 11, color: '#A9C2C9', letterSpacing: '0.06em' }}>Cover swings open; items on the left page, the chosen one on the right; bookmark tabs for sections</span>
            <button type="button" onClick={() => { setClosed(false); setReplay(n => n + 1); }} style={{ marginLeft: 'auto', fontFamily: PX_FONT, fontSize: 11, padding: '6px 12px', cursor: 'pointer' }}>Replay opening</button>
            <button type="button" onClick={() => setClosed(c => !c)} style={{ fontFamily: PX_FONT, fontSize: 11, padding: '6px 12px', cursor: 'pointer' }}>{closed ? 'Open book' : 'Close book'}</button>
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginBottom: 6 }}>
            {(['resources', 'readiness'] as const).map(b => (
              <button key={b} type="button" onClick={() => { setWhich(b); setClosed(false); setReplay(n => n + 1); }} style={{ fontFamily: PX_FONT, fontSize: 11, padding: '6px 12px', cursor: 'pointer', fontWeight: which === b ? 700 : 400 }}>{b}</button>
            ))}
          </div>
          {which === 'resources' ? <ResourcesBook replay={replay} closed={closed} /> : <ReadinessBook replay={replay} closed={closed} />}
        </section>
        {CONCEPTS.map((c, i) => (
          <section key={c.name}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 14, marginBottom: 10 }}>
              <span style={{ fontFamily: TITLE_FONT, fontSize: 26, color: '#EEF5F7', textShadow: '0 2px 0 #020A10' }}>{i + 1}. {c.name}</span>
              <span style={{ fontFamily: PX_FONT, fontSize: 11, color: '#A9C2C9', letterSpacing: '0.06em' }}>{c.note}</span>
            </div>
            {c.body}
          </section>
        ))}
      </main>
    </div>
  );
}
